@if (showLabel()) { <span class="field-label">{{ field().name }}</span> }
@if (!persisted()) {
  <small class="error" role="alert">{{ t('itemList.unreadable') }}</small>
} @else if (compact()) {
  <button type="button" class="summary-button" (click)="showEditor($event)"
    [attr.aria-label]="field().name + ': ' + (items().length ? summary() : t('itemList.add'))"
    [title]="t('itemList.open')">
    @if (items().length) { {{ summary() }} } @else { <lucide-icon name="plus" [size]="16" aria-hidden="true" /> }
  </button>
} @else {
  <section class="items-preview">
    @if (items().length) {
      <header class="preview-header">
        <span>{{ summary() }}</span>
        <button type="button" class="icon-button" (click)="showEditor($event)"
          [attr.aria-label]="t(field().readOnly ? 'itemList.view' : 'itemList.edit')"
          [title]="t(field().readOnly ? 'itemList.view' : 'itemList.edit')">
          <lucide-icon [name]="field().readOnly ? 'list' : 'pencil'" [size]="16" aria-hidden="true" />
        </button>
      </header>
      <div class="table-scroll">
        <table class="preview-table">
          <thead><tr><th>{{ t('itemList.description') }}</th><th>{{ t('itemList.quantity') }}</th><th>{{ t('itemList.unitPrice') }}</th><th>{{ t('itemList.total') }}</th></tr></thead>
          <tbody>
            @for (row of items().slice(0, 3); track $index) {
              <tr><td>{{ row.description }}</td><td>{{ quantity(row.quantity) }}</td><td>{{ money(row.unitPriceCents) }}</td><td>{{ total(row) }}</td></tr>
            }
          </tbody>
        </table>
      </div>
      @if (items().length > 3) {
        <button type="button" class="text-button more-button" (click)="showEditor($event)">{{ t('itemList.viewAll', { count: items().length }) }}</button>
      }
    } @else {
      <div class="empty-preview">
        <span>{{ t('itemList.empty') }}</span>
        @if (!field().readOnly) {
          <button type="button" class="icon-button" (click)="showEditor($event)" [attr.aria-label]="t('itemList.add')" [title]="t('itemList.add')">
            <lucide-icon name="plus" [size]="16" aria-hidden="true" />
          </button>
        }
      </div>
    }
  </section>
}
@if (open()) {
  <app-modal [visible]="true" size="lg" [title]="field().name" [subtitle]="t('itemList.help')"
    [stackAbove]="true" [closeOnEscape]="false" [closeOnOverlay]="false"
    (closed)="close()" (keydown)="onDialogKeydown($event)">
    <div modalBody>
      <div class="item-editor">
        @for (row of draft(); track row.id; let index = $index) {
          <div class="editor-row" role="group" [attr.aria-label]="t('itemList.rowLabel', { index: index + 1 })" [class.editor-row--invalid]="attempted() && error(row)">
            <div class="row-number" aria-hidden="true">{{ index + 1 }}</div>
            <label class="description-input">
              <input type="text" [value]="row.description" [maxLength]="maxDescription" [readOnly]="field().readOnly" [attr.aria-invalid]="attempted() && !!error(row)"
                [attr.aria-label]="t('itemList.description')" [placeholder]="t('itemList.description')" (input)="updateRow(row.id, 'description', $event)" />
            </label>
            <label>
              <input type="text" inputmode="decimal" maxlength="20" [value]="row.quantity" [readOnly]="field().readOnly" [attr.aria-invalid]="attempted() && !!error(row)"
                [attr.aria-label]="t('itemList.quantity')" [placeholder]="t('itemList.quantity')"
                (input)="updateRow(row.id, 'quantity', $event)" />
            </label>
            <app-cf-number-field [field]="priceField()" [value]="priceValue(row)" [showLabel]="false"
              (valueChanged)="onPriceChange(row.id, $event)" />
            <div class="row-total"><span>{{ t('itemList.total') }}</span><strong>{{ rowTotal(index) }}</strong></div>
            @if (!field().readOnly) {
              <button type="button" class="icon-button remove-button" [attr.aria-label]="t('itemList.remove', { index: index + 1 })" [title]="t('itemList.remove', { index: index + 1 })" (click)="removeRow(row.id)">
                <lucide-icon name="x" [size]="16" aria-hidden="true" />
              </button>
            }
            @if (attempted() && error(row); as message) { <small class="error row-error" role="alert">{{ message }}</small> }
          </div>
        } @empty { <p class="empty-copy">{{ t('itemList.empty') }}</p> }
      </div>
      <div class="editor-bottom">
        @if (!field().readOnly) {
          <button type="button" class="icon-button" [disabled]="draft().length >= maxRows" (click)="addRow()" [attr.aria-label]="t('itemList.addRow')" [title]="t('itemList.addRow')">
            <lucide-icon name="plus" [size]="16" aria-hidden="true" />
          </button>
          <small>{{ t('itemList.limit', { count: maxRows }) }}</small>
        }
      </div>
      @if (attempted() && invalid() && !draft().length) { <small class="error" role="alert">{{ t('itemList.required') }}</small> }
      @if (attempted() && invalid() && draft().length && !draftRows().includes(null)) { <small class="error" role="alert">{{ t('itemList.tooLong') }}</small> }
      <div class="grand-total"><span>{{ t('itemList.grandTotal') }}</span><strong>{{ draftRows().includes(null) ? '—' : money(draftTotal()) }}</strong></div>
    </div>
    <div modalFooter class="editor-footer">
      <button type="button" class="secondary-button" (click)="close()">{{ t(field().readOnly ? 'common.close' : 'common.cancel') }}</button>
      @if (!field().readOnly) { <button type="button" class="primary-button" (click)="apply()">{{ t('itemList.apply') }}</button> }
    </div>
  </app-modal>
}
